import React, { useState } from "react";
import {
  Box,
  Container,
  Grid,
  Typography,
  TextField,
  Button,
  Paper,
  Stack,
  Card,
  CardContent,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import WorkIcon from "@mui/icons-material/Work";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import PeopleIcon from "@mui/icons-material/People";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import SchoolIcon from "@mui/icons-material/School";

const Careers = () => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    position: "",
    experience: "",
    resume: null,
    coverLetter: "",
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFileChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      resume: e.target.files[0],
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log("Application submitted:", formData);
    // Add your form submission logic here
  };

  // Hero Section
  const HeroSection = () => (
    <Box
      sx={{
        background: "linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)",
        color: "white",
        py: { xs: 8, md: 12 },
        position: "relative",
        overflow: "hidden",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background:
            "radial-gradient(circle at 20% 50%, rgba(255,255,255,0.1) 0%, transparent 50%)",
        },
      }}
    >
      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
        <Typography
          variant="h2"
          sx={{
            fontWeight: 900,
            fontSize: { xs: "2.5rem", md: "3.5rem" },
            mb: 2,
          }}
        >
          Join Our Team
        </Typography>
        <Typography
          variant="h6"
          sx={{
            mt: 2,
            opacity: 0.95,
            fontSize: { xs: "1.1rem", md: "1.3rem" },
            maxWidth: "800px",
          }}
        >
          Build your career with us and be part of a dynamic team delivering
          innovative solutions across various industries
        </Typography>
      </Container>
    </Box>
  );

  // Why Join Us Section
  const WhyJoinUsSection = () => {
    const benefits = [
      {
        icon: <PeopleIcon sx={{ fontSize: 40 }} />,
        title: "Collaborative Culture",
        description:
          "Work with talented professionals in a supportive and inclusive environment",
      },
      {
        icon: <TrendingUpIcon sx={{ fontSize: 40 }} />,
        title: "Career Growth",
        description:
          "Access continuous learning opportunities and clear career advancement paths",
      },
      {
        icon: <AttachMoneyIcon sx={{ fontSize: 40 }} />,
        title: "Competitive Benefits",
        description:
          "Enjoy competitive salaries, health benefits, and comprehensive compensation packages",
      },
      {
        icon: <SchoolIcon sx={{ fontSize: 40 }} />,
        title: "Learning & Development",
        description:
          "Access to training programs, certifications, and skill development workshops",
      },
    ];

    return (
      <Box sx={{ py: { xs: 8, md: 10 }, bgcolor: "background.paper" }}>
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            sx={{
              fontWeight: 700,
              mb: 2,
              textAlign: "center",
              color: "#2a5298",
            }}
          >
            Why Join Us?
          </Typography>
          <Typography
            variant="body1"
            sx={{
              textAlign: "center",
              mb: 6,
              color: "text.secondary",
              maxWidth: "700px",
              mx: "auto",
            }}
          >
            We're committed to creating an environment where our team members
            can thrive, grow, and make a meaningful impact
          </Typography>

          <Grid container spacing={4}>
            {benefits.map((benefit, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Card
                  sx={{
                    height: "100%",
                    textAlign: "center",
                    p: 2,
                    transition: "transform 0.3s ease, box-shadow 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-8px)",
                      boxShadow: 6,
                    },
                  }}
                >
                  <CardContent>
                    <Box
                      sx={{
                        color: "primary.main",
                        mb: 2,
                        display: "flex",
                        justifyContent: "center",
                      }}
                    >
                      {benefit.icon}
                    </Box>
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 600, mb: 1.5, color: "#1e3c72" }}
                    >
                      {benefit.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {benefit.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>
    );
  };

  // Current Openings Section
  const CurrentOpeningsSection = () => {
    const jobOpenings = [
      {
        title: "Senior Software Engineer",
        department: "Technology",
        location: "Remote / Hybrid",
        type: "Full-time",
        experience: "5+ years",
        description:
          "We're looking for an experienced software engineer to join our development team and work on cutting-edge projects.",
        requirements: [
          "5+ years of experience in software development",
          "Proficiency in React, Node.js, and modern web technologies",
          "Strong problem-solving and communication skills",
          "Experience with cloud platforms (AWS/Azure)",
        ],
      },
      {
        title: "Healthcare Staffing Specialist",
        department: "Healthcare",
        location: "On-site",
        type: "Full-time",
        experience: "3+ years",
        description:
          "Join our healthcare staffing team to connect talented medical professionals with leading healthcare facilities.",
        requirements: [
          "3+ years in healthcare recruitment or staffing",
          "Knowledge of healthcare industry regulations",
          "Excellent interpersonal and negotiation skills",
          "Bachelor's degree in HR, Healthcare Administration, or related field",
        ],
      },
      {
        title: "Business Analyst",
        department: "Banking & Finance",
        location: "Hybrid",
        type: "Full-time",
        experience: "4+ years",
        description:
          "Seeking a detail-oriented business analyst to bridge the gap between business needs and technical solutions.",
        requirements: [
          "4+ years of experience in business analysis",
          "Strong analytical and documentation skills",
          "Experience in banking or financial services",
          "Proficiency in requirements gathering and process modeling",
        ],
      },
      {
        title: "Digital Marketing Manager",
        department: "Marketing",
        location: "Remote",
        type: "Full-time",
        experience: "5+ years",
        description:
          "Lead our digital marketing efforts and develop strategies to enhance our online presence and drive growth.",
        requirements: [
          "5+ years in digital marketing or related field",
          "Expertise in SEO, SEM, social media, and content marketing",
          "Strong data analysis and campaign management skills",
          "Experience with marketing automation tools",
        ],
      },
    ];

    return (
      <Box sx={{ py: { xs: 8, md: 10 } }}>
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            sx={{
              fontWeight: 700,
              mb: 2,
              textAlign: "center",
              color: "#2a5298",
            }}
          >
            Current Openings
          </Typography>
          <Typography
            variant="body1"
            sx={{
              textAlign: "center",
              mb: 6,
              color: "text.secondary",
              maxWidth: "700px",
              mx: "auto",
            }}
          >
            Explore exciting opportunities to grow your career with us
          </Typography>

          <Stack spacing={3}>
            {jobOpenings.map((job, index) => (
              <Accordion
                key={index}
                sx={{
                  boxShadow: 2,
                  "&:before": { display: "none" },
                  borderRadius: 2,
                  overflow: "hidden",
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    bgcolor: "background.paper",
                    "&:hover": { bgcolor: "#f5f5f5" },
                  }}
                >
                  <Grid container alignItems="center" spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Typography
                        variant="h6"
                        sx={{ fontWeight: 600, color: "#1e3c72" }}
                      >
                        {job.title}
                      </Typography>
                      <Chip
                        label={job.department}
                        size="small"
                        sx={{ mt: 0.5 }}
                        color="primary"
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Stack
                        direction="row"
                        spacing={2}
                        flexWrap="wrap"
                        sx={{ gap: 1 }}
                      >
                        <Chip
                          icon={<LocationOnIcon />}
                          label={job.location}
                          size="small"
                          variant="outlined"
                        />
                        <Chip
                          icon={<AccessTimeIcon />}
                          label={job.type}
                          size="small"
                          variant="outlined"
                        />
                        <Chip
                          icon={<WorkIcon />}
                          label={job.experience}
                          size="small"
                          variant="outlined"
                        />
                      </Stack>
                    </Grid>
                  </Grid>
                </AccordionSummary>
                <AccordionDetails sx={{ bgcolor: "#fafafa", p: 3 }}>
                  <Typography
                    variant="body1"
                    sx={{ mb: 3, color: "text.primary" }}
                  >
                    {job.description}
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 600, mb: 2, color: "#1e3c72" }}
                  >
                    Requirements:
                  </Typography>
                  <ul style={{ marginLeft: "20px" }}>
                    {job.requirements.map((req, idx) => (
                      <li key={idx}>
                        <Typography
                          variant="body2"
                          sx={{ mb: 1, color: "text.secondary" }}
                        >
                          {req}
                        </Typography>
                      </li>
                    ))}
                  </ul>
                  <Button
                    variant="contained"
                    sx={{
                      mt: 3,
                      px: 4,
                      py: 1.5,
                      background:
                        "linear-gradient(135deg, #2563EB 0%, #1e40af 100%)",
                    }}
                  >
                    Apply Now
                  </Button>
                </AccordionDetails>
              </Accordion>
            ))}
          </Stack>
        </Container>
      </Box>
    );
  };

  // Application Form Section
  const ApplicationFormSection = () => (
    <Box sx={{ py: { xs: 8, md: 10 }, bgcolor: "background.paper" }}>
      <Container maxWidth="md">
        <Typography
          variant="h3"
          sx={{
            fontWeight: 700,
            mb: 2,
            textAlign: "center",
            color: "#2a5298",
          }}
        >
          Apply for a Position
        </Typography>
        <Typography
          variant="body1"
          sx={{
            textAlign: "center",
            mb: 6,
            color: "text.secondary",
          }}
        >
          Submit your application and join our talented team
        </Typography>

        <Paper sx={{ p: 4, boxShadow: 3 }}>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="First Name"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  required
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Last Name"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  required
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Phone Number"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  required
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Position Applied For"
                  name="position"
                  value={formData.position}
                  onChange={handleInputChange}
                  required
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Years of Experience"
                  name="experience"
                  type="number"
                  value={formData.experience}
                  onChange={handleInputChange}
                  required
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12}>
                <Button
                  variant="outlined"
                  component="label"
                  fullWidth
                  sx={{ py: 2 }}
                >
                  Upload Resume (PDF or Word)
                  <input
                    type="file"
                    hidden
                    accept=".pdf,.doc,.docx"
                    onChange={handleFileChange}
                  />
                </Button>
                {formData.resume && (
                  <Typography
                    variant="body2"
                    sx={{ mt: 1, color: "text.secondary" }}
                  >
                    Selected: {formData.resume.name}
                  </Typography>
                )}
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Cover Letter"
                  name="coverLetter"
                  value={formData.coverLetter}
                  onChange={handleInputChange}
                  multiline
                  rows={6}
                  placeholder="Tell us why you're a great fit for this position..."
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12}>
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  size="large"
                  sx={{
                    py: 2,
                    fontSize: "1.1rem",
                    background:
                      "linear-gradient(135deg, #2563EB 0%, #1e40af 100%)",
                    "&:hover": {
                      background:
                        "linear-gradient(135deg, #1e40af 0%, #1e3a8a 100%)",
                    },
                  }}
                >
                  Submit Application
                </Button>
              </Grid>
            </Grid>
          </form>
        </Paper>
      </Container>
    </Box>
  );

  return (
    <Box>
      <HeroSection />
      <WhyJoinUsSection />
      <CurrentOpeningsSection />
      {/* <ApplicationFormSection /> */}
    </Box>
  );
};

export default Careers;
